import { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Smartphone, Monitor, Tablet, Laptop, ShieldCheck, ShieldOff,
  Search, Ban, Pencil, Check, X, Clock, MapPin,
} from "lucide-react";
import { toast } from "sonner";

function DeviceIcon({ type, className }) {
  const map = { mobile: Smartphone, tablet: Tablet, desktop: Laptop };
  const I = map[type] || Monitor;
  return <I className={className} />;
}

function relTime(iso) {
  if (!iso) return "—";
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `hace ${Math.floor(sec)}s`;
  if (sec < 3600) return `hace ${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)}h`;
  return `hace ${Math.floor(sec / 86400)}d`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

export default function AuthorizedDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [confirm, setConfirm] = useState(null); // device to revoke
  const [editing, setEditing] = useState(null); // device being renamed
  const [label, setLabel] = useState("");

  const load = async () => {
    try {
      const params = status !== "all" ? { status } : {};
      const { data } = await api.get("/admin/user-devices", { params });
      setDevices(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filtered = useMemo(() => {
    if (!search.trim()) return devices;
    const s = search.toLowerCase();
    return devices.filter((d) =>
      [d.user_email, d.user_name, d.device_name, d.os_name, d.browser_name]
        .filter(Boolean).join(" ").toLowerCase().includes(s)
    );
  }, [devices, search]);

  const revoke = async (d) => {
    try {
      await api.post(`/admin/user-devices/${d.id}/revoke`);
      toast.success("Dispositivo revocado");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveLabel = async (d) => {
    try {
      await api.patch(`/admin/user-devices/${d.id}`, { device_name: label });
      toast.success("Etiqueta actualizada");
      setEditing(null);
      setLabel("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeCount = devices.filter((d) => d.is_active).length;
  const revokedCount = devices.length - activeCount;

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Dispositivos Autorizados"
        subtitle="Bloqueo de un dispositivo por usuario. Solo el dispositivo autorizado puede iniciar sesión."
        testId="authorized-devices-header"
      />

      <div className="p-6 sm:p-8 max-w-6xl space-y-4" data-testid="authorized-devices-page">
        {/* Summary strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Activos</div>
            <div className="text-3xl font-black font-mono mt-1 text-slate-950">{activeCount}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Revocados</div>
            <div className="text-3xl font-black font-mono mt-1 text-slate-500">{revokedCount}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4 col-span-2 sm:col-span-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Política</div>
            <div className="text-xs font-semibold text-slate-900 mt-1 leading-tight">
              1 dispositivo autorizado por usuario. Revocar permite registrar uno nuevo.
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por usuario, correo, dispositivo…"
              className="pl-9 rounded-sm border-slate-300 h-10"
              data-testid="devices-search"
            />
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-sm p-1">
            {[
              { k: "all", l: "Todos" },
              { k: "active", l: "Activos" },
              { k: "revoked", l: "Revocados" },
            ].map((o) => (
              <button
                key={o.k}
                onClick={() => setStatus(o.k)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm ${
                  status === o.k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"
                }`}
                data-testid={`filter-${o.k}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
            <div className="col-span-3">Usuario</div>
            <div className="col-span-3">Dispositivo</div>
            <div className="col-span-2">IP / Red</div>
            <div className="col-span-2">Última conexión</div>
            <div className="col-span-1">Estado</div>
            <div className="col-span-1 text-right">Acción</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="devices-empty">
              No hay dispositivos registrados.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((d) => (
                <li key={d.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-slate-50" data-testid={`device-row-${d.id}`}>
                  <div className="md:col-span-3 min-w-0">
                    <div className="font-bold text-slate-950 truncate">{d.user_name || "—"}</div>
                    <div className="text-xs text-slate-500 font-mono truncate">{d.user_email}</div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 mt-0.5">{d.user_role}</div>
                  </div>

                  <div className="md:col-span-3 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${d.is_active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                        <DeviceIcon type={d.device_type} className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        {editing === d.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              autoFocus
                              value={label}
                              onChange={(e) => setLabel(e.target.value)}
                              className="h-7 text-xs rounded-sm"
                              data-testid={`label-input-${d.id}`}
                            />
                            <button onClick={() => saveLabel(d)} className="p-1 text-green-700 hover:bg-green-50 rounded-sm" data-testid={`label-save-${d.id}`}>
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => { setEditing(null); setLabel(""); }} className="p-1 text-slate-500 hover:bg-slate-100 rounded-sm">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 min-w-0">
                            <div className="font-semibold text-sm text-slate-900 truncate">{d.device_name || "Dispositivo"}</div>
                            <button onClick={() => { setEditing(d.id); setLabel(d.device_name || ""); }} className="p-1 text-slate-400 hover:text-slate-900 rounded-sm shrink-0" title="Renombrar" data-testid={`label-edit-${d.id}`}>
                              <Pencil className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 font-mono truncate">
                          {d.os_name} · {d.browser_name}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 text-xs font-mono text-slate-700 min-w-0">
                    <div className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {d.last_ip || "—"}</div>
                    <div className="text-[10px] text-slate-400 truncate">Primera: {d.first_ip || "—"}</div>
                  </div>

                  <div className="md:col-span-2 text-xs">
                    <div className="flex items-center gap-1 text-slate-700"><Clock className="w-3 h-3" /> {relTime(d.last_login_at)}</div>
                    <div className="text-[10px] text-slate-400 font-mono">Alta: {fmtDate(d.first_seen_at)}</div>
                  </div>

                  <div className="md:col-span-1">
                    {d.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-green-100 text-green-800 px-2 py-1 rounded-sm">
                        <ShieldCheck className="w-3 h-3" /> Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-slate-200 text-slate-600 px-2 py-1 rounded-sm">
                        <ShieldOff className="w-3 h-3" /> Revocado
                      </span>
                    )}
                  </div>

                  <div className="md:col-span-1 md:text-right">
                    {d.is_active ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirm(d)}
                        className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`revoke-btn-${d.id}`}
                      >
                        <Ban className="w-3 h-3 mr-1" /> Revocar
                      </Button>
                    ) : (
                      <span className="text-[10px] font-mono text-slate-400">
                        {d.revoked_at ? fmtDate(d.revoked_at) : ""}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Confirm revoke modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="revoke-modal">
          <div className="bg-white rounded-sm max-w-md w-full p-6 border border-slate-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <Ban className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-black text-lg text-slate-950 tracking-tight">Revocar dispositivo</h3>
                <p className="text-sm text-slate-600 mt-2">
                  <span className="font-semibold">{confirm.user_email}</span> quedará sin dispositivo autorizado.
                  En su próximo inicio de sesión podrá registrar un dispositivo nuevo.
                </p>
                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs font-mono text-slate-700">
                  {confirm.device_name} · {confirm.os_name} · {confirm.browser_name}
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirm(null)} className="rounded-sm">Cancelar</Button>
              <Button
                onClick={() => revoke(confirm)}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold"
                data-testid="revoke-confirm"
              >
                Revocar dispositivo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
